import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Paper,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  People as PeopleIcon,
  Warning as WarningIcon,
  Groups as GroupsIcon,
  FitnessCenter as CheckInIcon,
  Payment as PaymentIcon,
  PersonOutline as OutsideMemberIcon,
  Home as EstateIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useSocket } from '../contexts/SocketContext';
import apiService from '../services/api';
import { MemberStats } from '../types';
import CheckInModal from '../components/CheckInModal/CheckInModal';
import CheckInsSummaryCard from '../components/CheckInsSummaryCard';

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const { socket, memberCheckedIn, clearMemberCheckedIn } = useSocket();
  const [stats, setStats] = useState<MemberStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCheckInModal, setShowCheckInModal] = useState(false);
  const [checkInsRefreshTrigger, setCheckInsRefreshTrigger] = useState(0);

  useEffect(() => {
    loadStats();
  }, []);

  useEffect(() => {
    if (socket) {
      // Listen for real-time stats updates
      socket.on('stats:updated', (updatedStats: MemberStats) => {
        setStats(updatedStats);
      });

      socket.on('member:created', () => {
        loadStats(); // Refresh stats when new member is added
      });

      socket.on('member:updated', () => {
        loadStats(); // Refresh stats when a member is updated
      });

      // Listen for member check-ins to refresh the check-ins list
      socket.on('member-checked-in', () => {
        setCheckInsRefreshTrigger(prev => prev + 1); // Trigger refresh for check-ins list
      });
    }

    return () => {
      if (socket) {
        socket.off('stats:updated');
        socket.off('member:created');
        socket.off('member:updated');
        socket.off('member-checked-in');
      }
    };
  }, [socket]);

  // Show check-in modal when a member checks in
  useEffect(() => {
    if (memberCheckedIn) {
      setShowCheckInModal(true);
    } else {
      setShowCheckInModal(false);
    }
  }, [memberCheckedIn]);

  // Ensure modal shows when page regains focus if there's a pending check-in
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden && memberCheckedIn && !showCheckInModal) {
        console.log('Page became visible with pending check-in, showing modal');
        setShowCheckInModal(true);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [memberCheckedIn, showCheckInModal]);

  const handleCloseCheckInModal = () => {
    setShowCheckInModal(false);
    clearMemberCheckedIn();
  };

  const loadStats = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiService.getMemberStats();
      setStats(response);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load statistics');
      console.error('Error loading stats:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box 
        display="flex" 
        justifyContent="center" 
        alignItems="center" 
        minHeight="calc(100vh - 170px)"
        sx={{ 
          background: 'linear-gradient(135deg, #f9fafb 0%, #fafbfc 100%)'
        }}
      >
        <CircularProgress sx={{ color: '#1976d2' }} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ 
        background: 'linear-gradient(135deg, #f8fafc 0%, #ffffff 100%)',
        minHeight: 'calc(100vh - 170px)',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        p: 3
      }}>
        <Alert severity="error" sx={{ 
          mb: 2,
          backgroundColor: 'rgba(229, 62, 62, 0.1)',
          border: '1px solid rgba(229, 62, 62, 0.3)',
          color: '#e53e3e',
          borderRadius: 2,
          maxWidth: 500
        }}>
          {error}
        </Alert>
        <Button 
          onClick={loadStats} 
          variant="contained"
          sx={{
            background: 'linear-gradient(45deg, #1976d2 30%, #42a5f5 90%)',
            color: '#ffffff',
            fontWeight: 600,
            borderRadius: 2,
            '&:hover': {
              background: 'linear-gradient(45deg, #1565c0 30%, #1976d2 90%)',
            },
          }}
        >
          Retry
        </Button>
      </Box>
    );
  }

  if (!stats) {
    return (
      <Box sx={{ 
        background: 'linear-gradient(135deg, #f9fafb 0%, #fafbfc 100%)',
        minHeight: 'calc(100vh - 170px)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        p: 3
      }}>
        <Alert severity="warning" sx={{
          backgroundColor: 'rgba(237, 137, 54, 0.1)',
          border: '1px solid rgba(237, 137, 54, 0.3)',
          color: '#ed8936',
          borderRadius: 2,
          maxWidth: 500
        }}>
          No statistics available
        </Alert>
      </Box>
    );
  }

  const statCards = [
    {
      title: 'Total Active Members',
      value: stats.totalMembers,
      icon: <PeopleIcon sx={{ fontSize: 40 }} />,
      color: '#1976d2',
    },
    {
      title: 'Outside Members',
      value: stats.outsideMembers,
      icon: <OutsideMemberIcon sx={{ fontSize: 40 }} />,
      color: '#4caf50',
    },
    {
      title: 'Home Owners & Tenants',
      value: stats.estateMembers,
      icon: <EstateIcon sx={{ fontSize: 40 }} />,
      color: '#2e7d32',
    },
    {
      title: 'Expiring Soon',
      value: stats.expiringSoon,
      icon: <WarningIcon sx={{ fontSize: 40 }} />,
      color: '#f57c00',
    },
    {
      title: 'Expired',
      value: stats.expired,
      icon: <WarningIcon sx={{ fontSize: 40 }} />,
      color: '#d32f2f',
    },
  ];


  return (
    <Box sx={{
      background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
      minHeight: 'calc(100vh - 170px)',
      color: '#1e293b',
      px: { xs: 1, sm: 2, md: 4 },
      py: { xs: 2, sm: 3, md: 4 },
    }}>
      {/* Statistics Cards */}
      <Box
        display="grid"
        gridTemplateColumns={{ xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)', lg: 'repeat(5, 1fr)' }}
        gap={3}
        mb={4}
      >
        {statCards.map((card, index) => (
          <Card key={index} sx={{
            minHeight: 140,
            background: 'linear-gradient(135deg, #fff 0%, #f1f5f9 100%)',
            border: '2px solid #e2e8f0',
            borderRadius: 4,
            boxShadow: '0 4px 24px rgba(30, 41, 59, 0.08)',
            transition: 'all 0.3s',
            cursor: ['Total Active Members', 'Outside Members', 'Home Owners & Tenants', 'Expiring Soon', 'Expired'].includes(card.title) ? 'pointer' : 'default',
            '&:hover': {
              transform: 'translateY(-4px) scale(1.03)',
              boxShadow: `0 12px 40px ${card.color}33`,
              borderColor: card.color,
            },
          }}
          onClick={() => {
            if (card.title === 'Total Active Members') {
              navigate('/members?status=active');
            } else if (card.title === 'Outside Members') {
              navigate('/members?type=Outside Member');
            } else if (card.title === 'Home Owners & Tenants') {
              navigate('/members?type=Estate Members');
            } else if (card.title === 'Expiring Soon') {
              navigate('/members?status=expiring-soon');
            } else if (card.title === 'Expired') {
              navigate('/members?status=expired');
            }
          }}>
            <CardContent sx={{ p: 3 }}>
              <Box display="flex" alignItems="center" justifyContent="space-between" height="100%">
                <Box>
                  <Typography
                    color="textSecondary"
                    gutterBottom
                    variant="body2"
                    sx={{
                      color: '#64748b',
                      fontSize: '0.95rem',
                      fontWeight: 600,
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                    }}
                  >
                    {card.title}
                  </Typography>
                  <Typography
                    variant="h2"
                    sx={{
                      fontWeight: 800,
                      color: card.color,
                      fontSize: '2.8rem',
                      lineHeight: 1.1,
                    }}
                  >
                    {card.value}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    color: card.color,
                    background: `${card.color}15`,
                    borderRadius: '50%',
                    p: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: `0 2px 8px ${card.color}22`,
                  }}
                >
                  {card.icon}
                </Box>
              </Box>
            </CardContent>
          </Card>
        ))}
      </Box>

      {/* Charts and Details */}
      <Box
        display="grid"
        gridTemplateColumns={{ xs: '1fr', md: '1fr 1fr' }}
        gap={3}
        mb={4}
      >
        {/* Check-ins Summary */}
        <CheckInsSummaryCard refreshTrigger={checkInsRefreshTrigger} />

        {/* Quick Actions */}
        <Paper
          sx={{
            p: 4,
            background: 'linear-gradient(135deg, #f1f5f9 0%, #fff 100%)',
            border: '2px solid #e2e8f0',
            borderRadius: 4,
            boxShadow: '0 4px 24px rgba(30, 41, 59, 0.08)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: 340,
            transition: 'all 0.3s',
            '&:hover': {
              borderColor: '#3b82f6',
              boxShadow: '0 8px 32px #3b82f622',
              transform: 'translateY(-2px) scale(1.01)',
            },
          }}
        >
          <Typography variant="h6" gutterBottom sx={{
            color: '#1e293b',
            fontWeight: 700,
            mb: 3,
            letterSpacing: '0.5px',
            textTransform: 'uppercase',
          }}>
            Quick Actions
          </Typography>
          <Box display="flex" flexDirection="column" gap={2} width="100%">
            <Button
              variant="outlined"
              startIcon={<GroupsIcon />}
              onClick={() => navigate('/members')}
              fullWidth
              sx={{
                borderRadius: 3,
                py: 1.7,
                borderColor: '#2563eb',
                color: '#2563eb',
                fontWeight: 700,
                fontSize: '1.1rem',
                letterSpacing: '0.5px',
                '&:hover': {
                  borderColor: '#3b82f6',
                  backgroundColor: 'rgba(59, 130, 246, 0.06)',
                },
              }}
            >
              View Members
            </Button>
            <Button
              variant="outlined"
              startIcon={<CheckInIcon />}
              onClick={() => navigate('/daily-checkins')}
              fullWidth
              sx={{
                borderRadius: 3,
                py: 1.7,
                borderColor: '#059669',
                color: '#059669',
                fontWeight: 700,
                fontSize: '1.1rem',
                letterSpacing: '0.5px',
                '&:hover': {
                  borderColor: '#10b981',
                  backgroundColor: 'rgba(16, 185, 129, 0.06)',
                },
              }}
            >
              View Daily Check-ins
            </Button>
            <Button
              variant="outlined"
              startIcon={<PaymentIcon />}
              onClick={() => navigate('/add-payment')}
              fullWidth
              sx={{
                borderRadius: 3,
                py: 1.7,
                borderColor: '#b91c1c',
                color: '#b91c1c',
                fontWeight: 700,
                fontSize: '1.1rem',
                letterSpacing: '0.5px',
                '&:hover': {
                  borderColor: '#ef4444',
                  backgroundColor: 'rgba(239, 68, 68, 0.06)',
                },
              }}
            >
              Add New Payment
            </Button>
          </Box>
        </Paper>
      </Box>

      {/* Check-in Modal */}
      {memberCheckedIn && (
        <CheckInModal
          open={showCheckInModal}
          onClose={handleCloseCheckInModal}
          member={memberCheckedIn.member}
          validation={memberCheckedIn.validation}
          timestamp={memberCheckedIn.timestamp}
        />
      )}
    </Box>
  );
};

export default Dashboard;